import React, { useState, useContext, useRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import axiosConn from '../../axiosConn';
import { useConfettiText } from '../../hooks/useConfettiText';
import useMobileView from '../../hooks/useMobileView';

const MAX_ATTEMPTS = 5;
const HINT_ATTEMPT = 3;
const MESSAGE_PARTICLE_COUNT = 1000;

const RiddleUnlock = ({
  clueId,
  clueText,
  hint,
  disabled = false,
  unlockedClues = [],
}) => {
  const { player } = useContext(AuthContext);
  const [answer, setAnswer] = useState('');
  const [status, setStatus] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const mobileView = useMobileView();
  const riddleUnlockRef = useRef(null);

  const [showConfettiText, ConfettiTextComponent] = useConfettiText(
    riddleUnlockRef,
    mobileView,
    '+5 JGK Riddle Unlocked!',
    MESSAGE_PARTICLE_COUNT,
    'riddleUnlock',
  );

  const playUnlockSound = () => {
    const audio = new Audio(
      'https://res.cloudinary.com/joegalaxy/video/upload/v1752624122/Futuristic_HUD_Sound_Design_Blake_Sanchez_touovl.mp3',
    );
    audio.currentTime = 0;
    audio.play();
    setTimeout(() => {
      audio.pause();
      audio.currentTime = 0;
    }, 5000);
  };
  //   console.log('Clue ID:', clueId, 'Unlocked Clues:', unlockedClues);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('');
    if (attempts >= MAX_ATTEMPTS) {
      setStatus('No more attempts! Please try again later.');
      return;
    }
    try {
      const res = await axiosConn.post(
        `/clues/unlock-riddle/${clueId}`,
        { answer },
        { headers: { Authorization: `Bearer ${player.token}` } },
      );
      setIsUnlocked(true);
      setStatus(res.data.message || 'Correct! Riddle unlocked!');
      playUnlockSound();
      showConfettiText(); // 👈 trigger confetti
    } catch (err) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setStatus(err.response?.data?.message || 'Incorrect, try again!');
      if (newAttempts === HINT_ATTEMPT) setShowHint(true);
    }
  };

  // World-class solved UI
  if (disabled) {
    return (
      <div
        ref={riddleUnlockRef}
        className="text-center text-success fw-bold"
        style={{
          fontSize: 28,
          margin: '18px 0',
          animation: 'pop 0.7s',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span role="img" aria-label="confetti" style={{ fontSize: 38 }}>
          🎉
        </span>
        <span style={{ fontSize: 22, color: '#ef4444', fontWeight: 700 }}>
          This riddle is already solved!
        </span>
        <ConfettiTextComponent />
        <span role="img" aria-label="trophy" style={{ fontSize: 32 }}>
          🏆
        </span>
        <span
          className="badge"
          style={{
            background: '#e5e7eb',
            color: '#ef4444',
            fontWeight: 700,
            fontSize: 15,
            borderRadius: 10,
            marginTop: 8,
            boxShadow: '0 2px 8px #ef444422',
            letterSpacing: 0.5,
            animation: 'pop 0.7s',
          }}
        >
          <span role="img" aria-label="check">
            ✔
          </span>{' '}
          Solved!
        </span>
        <style>
          {`
            @keyframes pop {
              0% { transform: scale(0.7); opacity: 0; }
              60% { transform: scale(1.2); opacity: 1; }
              100% { transform: scale(1); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div className="text-center">
      {/* <div className="mb-2" style={{ fontWeight: 500 }}>
        {clueText}
      </div> */}
      {isUnlocked ? (
        <div
          ref={riddleUnlockRef}
          className="text-success fw-bold"
          style={{
            fontSize: 28,
            margin: '18px 0',
            animation: 'pop 0.7s',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span role="img" aria-label="confetti" style={{ fontSize: 38 }}>
            🎉
          </span>
          <span>Unlocked!</span>
          <ConfettiTextComponent />
          <span role="img" aria-label="trophy" style={{ fontSize: 32 }}>
            🏆
          </span>
          <style>
            {`
              @keyframes pop {
                0% { transform: scale(0.7); opacity: 0; }
                60% { transform: scale(1.2); opacity: 1; }
                100% { transform: scale(1); }
              }
            `}
          </style>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="d-flex flex-column align-items-center"
        >
          <input
            type="text"
            className="form-control mb-2"
            placeholder="Type your answer..."
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            style={{ maxWidth: 220, textAlign: 'center' }}
            autoFocus
            autoComplete="off"
            disabled={attempts >= MAX_ATTEMPTS}
          />
          <button
            className="btn btn-primary"
            type="submit"
            disabled={!answer.trim() || attempts >= MAX_ATTEMPTS}
          >
            Submit
          </button>
        </form>
      )}
      {status && <div className="mt-2 small text-muted">{status}</div>}
      {!isUnlocked && (
        <div className="mt-2 small" style={{ color: '#e11d48' }}>
          Attempts: {attempts} / {MAX_ATTEMPTS}
        </div>
      )}
      {showHint && hint && !isUnlocked && (
        <div
          className="alert alert-warning mt-3"
          style={{
            fontSize: 15,
            borderRadius: 10,
            animation: 'hintPop 0.7s',
            background: '#fffbe6',
            color: '#b45309',
            border: '1.5px solid #f59e42',
            maxWidth: 320,
            margin: '0 auto',
          }}
        >
          💡 <strong>Hint:</strong> {hint}
          <style>
            {`
              @keyframes hintPop {
                0% { transform: scale(0.7); opacity: 0; }
                60% { transform: scale(1.1); opacity: 1; }
                100% { transform: scale(1); }
              }
            `}
          </style>
        </div>
      )}
    </div>
  );
};

export default RiddleUnlock;
